import { Injectable } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { PERMISSIONS_MAX_SECONDS } from '../models/permissions.models';

/**
 * Temporizador de espera de permisos de cámara y micrófono.
 *
 * `startWait` es **idempotente**: si ya hay una espera en curso o el KO ya se ha disparado,
 * no reinicia el reloj. Esto permite a los componentes llamarlo tanto al iniciar la petición
 * de permisos (para cubrir el prompt nativo) como en `onPermissionsDenied` sin reiniciarlo.
 *
 * Además, en navegadores que exponen `navigator.permissions.query({ name: 'camera' })`, se
 * monitoriza el cambio de estado: si el usuario concede los permisos en el prompt nativo,
 * el reloj se cancela de inmediato, sin esperar a que `getUserMedia` resuelva (apertura del
 * stream físico, que en hardware lento puede añadir segundos de latencia).
 *
 * El estado "ya hemos disparado el timeout" vive aquí (`hasHandledTimeout()`) para que los
 * componentes no tengan que duplicarlo. `stopWait()` resetea ambos estados, de modo que el
 * siguiente componente que use el singleton parte de cero.
 */
@Injectable({
	providedIn: 'root',
})
export class CameraPermissionsTimerService {
	private waitSub: Subscription | null = null;
	private timeoutHandled = false;
	private cameraPermissionStatus: PermissionStatus | null = null;
	private cameraPermissionChangeHandler: (() => void) | null = null;

	public isWaiting(): boolean {
		return !!this.waitSub;
	}

	public hasHandledTimeout(): boolean {
		return this.timeoutHandled;
	}

	public startWait(onTimeout: () => void): void {
		if (this.timeoutHandled || this.waitSub) {
			return;
		}
		this.waitSub = timer(PERMISSIONS_MAX_SECONDS * 1000).subscribe(() => {
			this.waitSub = null;
			this.timeoutHandled = true;
			onTimeout();
		});
		this.subscribeToCameraPermissionChange();
	}

	public stopWait(): void {
		if (this.waitSub) {
			this.waitSub.unsubscribe();
			this.waitSub = null;
		}
		this.unsubscribeFromCameraPermissionChange();
		this.timeoutHandled = false;
	}

	/**
	 * Suscribe a la API `navigator.permissions.query` para detectar el grant del usuario
	 * en cuanto el navegador lo confirma, sin depender de que `getUserMedia` haya resuelto.
	 * Si la API no está disponible (Safari/iOS, etc.), la suscripción se ignora y el reloj
	 * sigue funcionando como fallback.
	 */
	private subscribeToCameraPermissionChange(): void {
		if (!navigator.permissions?.query) {
			return;
		}
		navigator.permissions
			.query({ name: 'camera' as PermissionName })
			.then((status: PermissionStatus) => {
				if (!this.waitSub) {
					return;
				}
				if (status.state === 'granted') {
					this.stopWait();
					return;
				}
				this.cameraPermissionStatus = status;
				this.cameraPermissionChangeHandler = () => {
					if (status.state === 'granted') {
						this.stopWait();
					}
				};
				status.addEventListener('change', this.cameraPermissionChangeHandler);
			})
			.catch(() => undefined);
	}

	private unsubscribeFromCameraPermissionChange(): void {
		if (this.cameraPermissionStatus && this.cameraPermissionChangeHandler) {
			this.cameraPermissionStatus.removeEventListener('change', this.cameraPermissionChangeHandler);
		}
		this.cameraPermissionStatus = null;
		this.cameraPermissionChangeHandler = null;
	}
}
